import React, { useState, useRef, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Search input with AI-powered suggestion dropdown.
 * Reusable across BrandPage, ProductPage, Microsite, etc.
 */
export default function SearchWithSuggestions({
  value,
  onChange,
  onSubmit,
  placeholder = 'Search products...',
  className = '',
  inputClassName = '',
  testId = 'search-with-suggestions',
  disableSuggestions = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [focused, setFocused] = useState(false);
  const debRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (disableSuggestions) return;
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDrop(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [disableSuggestions]);

  // Debounced suggestion fetch — skipped when disableSuggestions is true
  useEffect(() => {
    if (disableSuggestions) return;
    if (debRef.current) clearTimeout(debRef.current);
    if (!value || value.trim().length < 2) {
      setSuggestions([]);
      setShowDrop(false);
      return;
    }
    debRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/nav/suggestions?q=${encodeURIComponent(value.trim())}`);
        const data = await res.json();
        const list = data.suggestions || [];
        setSuggestions(list);
        setShowDrop(list.length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 150);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [value, disableSuggestions]);

  // Pre-warm backend cache for top suggestions so clicking feels instant
  useEffect(() => {
    if (!suggestions.length) return;
    suggestions.slice(0, 3).forEach(s => {
      const q = (s.product_name || s || '').trim();
      if (q.length < 3) return;
      fetch(`${API}/api/chat/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, session_id: 'prefetch' }),
      }).catch(() => {});
    });
  }, [suggestions]);

  const selectSuggestion = (s) => {
    setShowDrop(false);
    setSuggestions([]);
    if (onSubmit) onSubmit(s);
    else if (onChange) onChange(s);
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef} data-testid={testId}>
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 pointer-events-none z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); setTimeout(() => setShowDrop(false), 150); }}
        onKeyDown={e => {
          if (e.key === 'Enter' && onSubmit) onSubmit(value);
          if (e.key === 'Escape') { setShowDrop(false); }
        }}
        placeholder={placeholder}
        className={`w-full pl-10 pr-4 py-3 text-sm border-2 border-gray-200 rounded-xl bg-white outline-none focus:border-blue-400 transition-colors ${inputClassName}`}
        data-testid={`${testId}-input`}
      />
      {value && (
        <button onClick={() => { onChange(''); setSuggestions([]); setShowDrop(false); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      {showDrop && focused && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-[200] overflow-hidden">
          <div className="max-h-[420px] overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onMouseDown={e => { e.preventDefault(); selectSuggestion(s.product_name || s); }}
              className="w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-start gap-2.5 transition-colors border-b border-gray-50 last:border-0"
            >
              <svg className="w-3 h-3 flex-shrink-0 mt-1" fill="none" viewBox="0 0 16 16">
                {s.is_partner
                  ? <path d="M8 1.5L9.2 6H13.5L10.1 8.8L11.3 13.5L8 10.8L4.7 13.5L5.9 8.8L2.5 6H6.8L8 1.5Z" fill="#60A5FA"/>
                  : (<><circle cx="7" cy="7" r="4.5" stroke="#D1D5DB" strokeWidth="1.5"/><path d="M10.5 10.5L13 13" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round"/></>)
                }
              </svg>
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-800 truncate">{s.product_name || s}</div>
                {(s.brand_name || s.catalog_number) && (
                  <div className="text-xs text-gray-400 truncate mt-0.5">
                    {s.brand_name}{s.brand_name && s.catalog_number ? ' · ' : ''}{s.catalog_number}
                  </div>
                )}
              </div>
            </button>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}
